import type { ToggleProps } from '@react-types/checkbox';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Checkbox, CheckboxAlign, CheckboxVerticalAlign, type CheckboxProps } from '@calm-web/design-system';

import { useIsCanadaDevice, useIsGDPRDevice, useIsUKGDPRDevice } from '@/hooks/user';

import messages from './messages';

export type MarketingOptInProps = Pick<ToggleProps, 'onChange'> &
	Partial<Pick<CheckboxProps, 'alignment' | 'verticalAlignment' | 'textSize' | 'className'>>;

const MarketingOptIn = ({
	onChange,
	alignment = CheckboxAlign.Center,
	verticalAlignment = CheckboxVerticalAlign.Center,
	textSize,
	className,
}: MarketingOptInProps): React.ReactElement | null => {
	const isGDPRDevice = useIsGDPRDevice();
	const isUKGDPRDevice = useIsUKGDPRDevice();
	const isCanadaDevice = useIsCanadaDevice();
	const displayCheckbox = isGDPRDevice || isUKGDPRDevice || isCanadaDevice;
	// This covers the gate already being known at mount. On pages with no server-preloaded
	// device state, though, geo resolves asynchronously - it can flip false -> true after this
	// first render - so we also need the render-time correction below to catch that case.
	const [isSelected, setIsSelected] = useState(!displayCheckbox);
	// "Adjusting state when a prop changes" (not an effect): setting state here re-renders
	// before paint, so a gated user whose geo resolves late never sees a checked-box flash.
	const [prevDisplayCheckbox, setPrevDisplayCheckbox] = useState(displayCheckbox);
	if (displayCheckbox !== prevDisplayCheckbox) {
		setPrevDisplayCheckbox(displayCheckbox);
		if (displayCheckbox) {
			setIsSelected(false);
		}
	}

	const { formatMessage } = useIntl();
	useEffect(() => {
		onChange?.(isSelected);
	}, [onChange, isSelected]);

	if (!displayCheckbox) {
		return null;
	}

	return (
		<Checkbox
			name="marketing-opt-in"
			onChange={val => {
				setIsSelected(val.currentTarget.checked);
				onChange?.(val.currentTarget.checked);
			}}
			checked={isSelected}
			alignment={alignment}
			verticalAlignment={verticalAlignment}
			textSize={textSize}
			className={className}
		>
			{formatMessage(messages.marketingOptIn)}
		</Checkbox>
	);
};

export default MarketingOptIn;
